import React from 'react';
import { Truck } from 'lucide-react';
import { useGeoCountry } from '../contexts/GeoCountryContext';

/**
 * Small "Ships to your country" ribbon shown on every product page.
 * Reads the visitor's country from the shared GeoCountryContext (one fetch
 * per session, cached). Auto-translates copy with the currentLanguage prop.
 */
const TEMPLATES = {
  en: { sep: '·', days: '3-7 days', fallback: 'Worldwide shipping' },
  es: { sep: '·', days: '3-7 días', fallback: 'Envío a todo el mundo' },
  ru: { sep: '·', days: '3-7 дней', fallback: 'Доставка по всему миру' },
  de: { sep: '·', days: '3-7 Tage', fallback: 'Weltweiter Versand' },
  fr: { sep: '·', days: '3-7 jours', fallback: 'Livraison mondiale' },
  it: { sep: '·', days: '3-7 giorni', fallback: 'Spedizione mondiale' },
  pl: { sep: '·', days: '3-7 dni', fallback: 'Wysyłka na cały świat' },
  nl: { sep: '·', days: '3-7 dagen', fallback: 'Wereldwijde verzending' },
  pt: { sep: '·', days: '3-7 dias', fallback: 'Envio mundial' },
  au: { sep: '·', days: '3-7 days', fallback: 'Worldwide shipping' },
};

const SUPPORTED_LANGS = ['en', 'es', 'ru', 'de', 'fr', 'it', 'pl', 'nl', 'pt', 'au'];

const ShippingRibbon = ({ currentLanguage = 'en', className = '' }) => {
  const data = useGeoCountry();
  const lang = SUPPORTED_LANGS.includes(currentLanguage) ? currentLanguage : 'en';
  const labels = TEMPLATES[lang];
  const headPhrase = data?.delivery_phrase?.[lang] || labels.fallback;
  const text = `${headPhrase} ${labels.sep} ${labels.days}`;

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-capsule-teal/10 to-capsule-aqua/10 border border-capsule-teal/25 text-capsule-teal-dark text-xs font-medium shadow-sm ${className}`}
      data-testid="shipping-ribbon"
      data-country-code={data?.country_code || ''}
      role="status"
      aria-live="polite"
    >
      <Truck className="w-3.5 h-3.5 text-capsule-teal flex-shrink-0" />
      <span data-testid="shipping-ribbon-text">{text}</span>
    </div>
  );
};

export default ShippingRibbon;
